import './index.styl'

import { isMobile, Text, TextStyle, Container } from 'pixi.js'
import FileSaver from 'file-saver'
import EDITOR, {
    Editor,
    Blueprint,
    Book,
    TrainBlueprintError,
    ModdedBlueprintError,
    CorruptedBlueprintStringError,
    BookWithNoBlueprintsError,
    encode,
    getBlueprintOrBookFromSource,
    generateFactory,
    loadFactory,
    neighborSeed,
    encodeFactory,
    generateFactoryFromSeed,
    hashTile,
    seedToBase36,
    base36ToSeed,
    getFactorioVersion,
} from '@fbe/editor'
import type { IEntity } from '@fbe/editor'
import { TileWorld, TILE_STRIDE } from '@fbe/editor'
import { initToasts } from './toasts'
import { initSettingsPane } from './settingsPane'

document.addEventListener('contextmenu', e => e.preventDefault())

const editor = new Editor()

let t0 = performance.now()

const CANVAS = document.getElementById('editor') as HTMLCanvasElement

let bp: Blueprint
let book: Book

const loadingScreen = {
    el: document.getElementById('loadingScreen'),
    show() {
        this.el.classList.add('active')
        t0 = performance.now()
    },
    hide() {
        this.el.classList.remove('active')
        const t1 = performance.now()
        if (editor.debug) {
            console.log('Load time:', t1 - t0)
        }
    },
}

console.log(
    '\n%cFactory of Babel — built on Factorio Blueprint Editor\nhttps://github.com/TobinCavanaugh/factorio-blueprint-editor\n',
    'color: #edbb6a; font-weight: bold'
)

const createToast = initToasts()

if (isMobile.any) {
    createToast({
        text:
            'Application is not compatible with mobile devices.<br>' +
            'If you think this is a mistake, feel free to report this bug on github or using the feedback button.',
        type: 'error',
        timeout: Infinity,
    })
    loadingScreen.el.classList.add('error')
    throw new Error('MOBILE_DEVICE_NOT_SUPPORTED')
}

if (typeof WebAssembly !== 'object' && typeof WebAssembly.instantiate !== 'function') {
    createToast({
        text:
            "Current browser doesn't support WebAssembly.<br>" +
            'If you think this is a mistake, feel free to report this bug on github or using the feedback button.',
        type: 'error',
        timeout: Infinity,
    })
    loadingScreen.el.classList.add('error')
    throw new Error('WEB_ASSEMBLY_NOT_SUPPORTED')
}

const params = window.location.search.slice(1).split('&')

let bpSource: string
let bpIndex = 0
for (const p of params) {
    if (p.includes('source')) {
        bpSource = p.split('=')[1]
    }
    if (p.includes('index')) {
        bpIndex = Number(p.split('=')[1])
    }
}

let changeBookForIndexSelector: (bpOrBook: Book | Blueprint) => void
let currentFactorySeed: bigint | undefined = 0n

// ---- Tile World (infinite grid of factories) ----
let tileWorld: TileWorld | undefined
let tileMode = true
let centerTX = 0
let centerTY = 0
let tilePollInterval: ReturnType<typeof setInterval> | undefined
let loadedTiles = new Set<string>()
let tileEntityMap = new Map<string, number[]>()
/** Custom entities injected at specific tiles (pasted blueprints override generated ones) */
let customTileEntities = new Map<string, IEntity[]>()
let TILE_RADIUS = Number(localStorage.getItem('tileRadius')) || 1

/** Map a factory seed bigint to a deterministic tile coordinate */
function seedToTileXY(seed: bigint): { tx: number; ty: number } {
    const tx = (Math.abs(Number(seed & 0x7fffffffn)) % 2000) - 1000
    const ty = (Math.abs(Number((seed >> 31n) & 0x7fffffffn)) % 2000) - 1000
    return { tx, ty }
}

/** Inject custom entities at a tile location so addTileEntities uses them instead of generated ones */
function injectCustomTile(tx: number, ty: number, entities: IEntity[]): void {
    const key = tx + ',' + ty
    customTileEntities.set(key, entities)
}

/** Navigate to a specific tile — enter tile mode, load surrounding tiles, center viewport */
async function navigateToTile(tx: number, ty: number): Promise<void> {
    // Hold the poll guard so checkTileChange can't race a navigation in progress
    tileSyncInFlight = true
    loadingScreen.show()
    try {
        if (!tileMode) {
            await enterTileMode()
        }
        await syncTiles(tx, ty)
        editor.centerViewportOn(
            tx * TILE_STRIDE * 32 + 8 * 32,
            ty * TILE_STRIDE * 32 + 8 * 32,
            Math.min(editor.getViewportScale(), 1.5)
        )
        loadingScreen.hide()
        createToast({ text: 'Navigated to tile (' + tx + ',' + ty + ')', type: 'success' })
    } catch (e) {
        loadingScreen.hide()
        createErrorMessage('Failed to navigate to tile.', e)
    } finally {
        tileSyncInFlight = false
    }
}

/** Decode a factory seed, inject it at its deterministic tile, and navigate there.
 *  Stays in the tile world so the tile location is always visible. */
async function injectSeedAndNavigate(seed: bigint): Promise<void> {
    const decoded = loadFactory(seed)
    const { tx, ty } = seedToTileXY(seed)

    // Make sure we're in the world BEFORE injecting (enterTileMode resets custom tiles)
    if (!tileMode) {
        await enterTileMode()
    }

    // If the target tile is already loaded, drop it so the custom factory replaces it
    const key = tx + ',' + ty
    if (loadedTiles.has(key)) {
        await removeTileEntities(tx, ty)
    }

    injectCustomTile(tx, ty, decoded.entities)
    await navigateToTile(tx, ty)
}

/** Extract tile coordinates (and optional seed) from a Blueprint label.
 *  Supports: "FoB (<tx>,<ty>) <b36>" and legacy "FoB <b36>" */
function extractFobTile(pastedBp: Blueprint): { tx: number; ty: number; seed?: bigint } | undefined {
    if (pastedBp.isEmpty()) return undefined

    // 1. New format: "FoB (<tx>,<ty>) <b36>"
    const newMatch = pastedBp.name.match(/^FoB \((-?\d+),(-?\d+)\)\s*(\S+)?/)
    if (newMatch) {
        const tx = parseInt(newMatch[1], 10)
        const ty = parseInt(newMatch[2], 10)
        const seedStr = newMatch[3]
        let seed: bigint | undefined
        if (seedStr) {
            try { seed = base36ToSeed(seedStr) } catch (_) { /* */ }
        }
        return { tx, ty, seed }
    }

    // 2. Legacy: "FoB <b36>" (no tile coords) — can't navigate, just return seed
    const legacyMatch = pastedBp.name.match(/^FoB ([0-9a-zA-Z]+)$/)
    if (legacyMatch) {
        try {
            const seed = base36ToSeed(legacyMatch[1])
            return { tx: undefined as unknown as number, ty: undefined as unknown as number, seed }
        } catch (_) { /* */ }
    }

    // 3. WeakMap cache
    const cached = bpEntityCache.get(pastedBp)
    if (cached) {
        return { tx: undefined as unknown as number, ty: undefined as unknown as number, seed: encodeFactory(cached) }
    }

    // 4. Fallback: snap positions to grid, encode, but we don't know the tile
    const serialized = pastedBp.serialize()
    const entities = serialized.entities
    if (!entities || entities.length === 0) return undefined

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    for (const e of entities) {
        minX = Math.min(minX, e.position.x)
        minY = Math.min(minY, e.position.y)
        maxX = Math.max(maxX, e.position.x)
        maxY = Math.max(maxY, e.position.y)
    }
    if (maxX - minX > 16 || maxY - minY > 16) return undefined

    const gridEntities: IEntity[] = entities.map((e, i) => ({
        entity_number: i + 1,
        name: e.name,
        position: { x: Math.round(e.position.x - minX), y: Math.round(e.position.y - minY) },
        direction: e.direction,
        recipe: e.recipe,
    }))
    return { tx: undefined as unknown as number, ty: undefined as unknown as number, seed: encodeFactory(gridEntities) }
}

function getVisibleWindow(cx: number, cy: number): { minTX: number; maxTX: number; minTY: number; maxTY: number } {
    return {
        minTX: cx - TILE_RADIUS,
        maxTX: cx + TILE_RADIUS,
        minTY: cy - TILE_RADIUS,
        maxTY: cy + TILE_RADIUS,
    }
}

/** Update how many tiles are visible around the current tile and reload the window */
function setTileRadius(radius: number): void {
    TILE_RADIUS = radius
    localStorage.setItem('tileRadius', radius.toString())
    if (tileMode && tileWorld) {
        void syncTiles(centerTX, centerTY)
    }
}

/** Yield to the browser so long tile-loading loops don't freeze the UI */
function yieldToUI(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0))
}

async function addTileEntities(tx: number, ty: number): Promise<void> {
    if (!bp) return
    const key = tx + ',' + ty
    if (loadedTiles.has(key)) return

    // Check for custom (pasted) entities first, then fall back to tile world generation
    const customEntities = customTileEntities.get(key)
    const entities = customEntities || (tileWorld ? tileWorld.getTile(tx, ty).entities : [])

    // Derive a seed: for custom tiles, encode the entities; for generated, use tile seed
    const seed = customEntities
        ? encodeFactory(customEntities)
        : (tileWorld ? tileWorld.getTile(tx, ty).seed : 0n)

    const entityNrs: number[] = []

    const prevLogging = bp.history.logging
    bp.history.logging = false
    bp.history.startTransaction('load tile ' + key)

    const offsetX = tx * TILE_STRIDE
    const offsetY = ty * TILE_STRIDE
    for (const e of entities) {
        const entity = bp.createEntity({
            name: e.name,
            position: { x: e.position.x + offsetX + 0.5, y: e.position.y + offsetY + 0.5 },
            direction: e.direction,
            recipe: e.recipe,
        })
        entityNrs.push(entity.entityNumber)
    }

    bp.history.commitTransaction()
    bp.history.reset()
    bp.history.logging = prevLogging

    tileEntityMap.set(key, entityNrs)
    loadedTiles.add(key)

    editor.addTileGrid(tx, ty)
    addTileLabel(tx, ty, seed)
}

async function removeTileEntities(tx: number, ty: number): Promise<void> {
    if (!bp) return
    const key = tx + ',' + ty
    if (!loadedTiles.has(key)) return

    const entityNrs = tileEntityMap.get(key) || []

    const prevLogging = bp.history.logging
    bp.history.logging = false
    bp.history.startTransaction('unload tile ' + key)

    for (const nr of entityNrs) {
        const entity = bp.entities.get(nr)
        if (entity) bp.removeEntity(entity)
    }

    bp.history.commitTransaction()
    bp.history.reset()
    bp.history.logging = prevLogging

    tileEntityMap.delete(key)
    loadedTiles.delete(key)

    editor.removeTileGrid(tx, ty)
    removeTileLabel(tx, ty)
}

async function syncTiles(newCX: number, newCY: number): Promise<void> {
    if (!tileWorld) return

    const win = getVisibleWindow(newCX, newCY)
    const newTiles = new Set<string>()
    for (let ty = win.minTY; ty <= win.maxTY; ty++) {
        for (let tx = win.minTX; tx <= win.maxTX; tx++) {
            newTiles.add(tx + ',' + ty)
        }
    }

    // Add new tiles
    const toAdd: [number, number][] = []
    for (const key of newTiles) {
        if (!loadedTiles.has(key)) {
            const [tx, ty] = key.split(',').map(Number)
            toAdd.push([tx, ty])
        }
    }

    // Remove stale tiles
    const toRemove: [number, number][] = []
    for (const key of loadedTiles) {
        if (!newTiles.has(key)) {
            const [tx, ty] = key.split(',').map(Number)
            toRemove.push([tx, ty])
        }
    }

    // Draw background grids for the whole window up front so the world is
    // visible immediately, even while entities are still streaming in
    for (const key of newTiles) {
        const [tx, ty] = key.split(',').map(Number)
        editor.addTileGrid(tx, ty)
    }

    // Yield occasionally (every 8 tiles) so large windows don't freeze the UI,
    // without paying a ~1-4ms timer delay per tile
    let count = 0
    for (const [tx, ty] of toAdd) {
        await addTileEntities(tx, ty)
        if (++count % 8 === 0) await yieldToUI()
    }
    for (const [tx, ty] of toRemove) {
        await removeTileEntities(tx, ty)
        if (++count % 8 === 0) await yieldToUI()
    }

    centerTX = newCX
    centerTY = newCY

    // Update panel info (prefer a custom-injected factory over the generated one)
    const activeKey = centerTX + ',' + centerTY
    const customEntities = customTileEntities.get(activeKey)
    const active = tileWorld.getActiveTile(centerTX, centerTY)
    const displaySeed = customEntities ? encodeFactory(customEntities) : active.seed
    const entityCount = customEntities ? customEntities.length : active.entities.length
    setFobSeed(displaySeed, 'Tile (' + centerTX + ',' + centerTY + ') \u2014 ' + entityCount + ' entities')
    const url = new URL(window.location.href)
    url.searchParams.set('factory', seedToBase36(displaySeed))
    window.history.replaceState({}, '', url.toString())
}

let tileSyncInFlight = false

function checkTileChange(): void {
    if (!tileMode || !tileWorld) return
    if (tileSyncInFlight) return // don't stack syncs while one is loading
    try {
        const center = editor.getWorldCenter()
        const { tx, ty } = TileWorld.worldToTile(center.x, center.y)
        if (tx !== centerTX || ty !== centerTY) {
            tileSyncInFlight = true
            void syncTiles(tx, ty).finally(() => {
                tileSyncInFlight = false
            })
        }
    } catch (_) { /* editor may not be ready */ }
}

async function enterTileMode(): Promise<void> {
    tileMode = true
    tileWorld = new TileWorld()
    loadedTiles = new Set()
    tileEntityMap = new Map()
    customTileEntities = new Map()

    // Initialize with empty blueprint so initBP registers entity listeners
    loadingScreen.show()
    bp = new Blueprint()
    book = undefined
    await editor.loadBlueprint(bp)
    changeBookForIndexSelector(bp)

    // Load initial tile window (yield occasionally so large windows don't freeze)
    centerTX = 0
    centerTY = 0
    const win = getVisibleWindow(0, 0)

    // Draw the whole window's background grids first so it appears immediately
    for (let ty = win.minTY; ty <= win.maxTY; ty++) {
        for (let tx = win.minTX; tx <= win.maxTX; tx++) {
            editor.addTileGrid(tx, ty)
        }
    }

    let count = 0
    for (let ty = win.minTY; ty <= win.maxTY; ty++) {
        for (let tx = win.minTX; tx <= win.maxTX; tx++) {
            await addTileEntities(tx, ty)
            if (++count % 8 === 0) await yieldToUI()
        }
    }

    loadingScreen.hide()

    const active = tileWorld.getActiveTile(0, 0)
    setFobSeed(active.seed, 'Tile (0,0) \u2014 ' + active.entities.length + ' entities')

    tilePollInterval = setInterval(checkTileChange, 250)
}

// ---- Tile seed labels (managed per-tile) ----
const tileLabelMap = new Map<string, Text>()
const labelStyle = new TextStyle({
    fontSize: 10,
    fill: 0xaaaaaa,
    fontFamily: 'monospace',
    align: 'center',
})

function addTileLabel(tx: number, ty: number, seed: bigint): void {
    const key = tx + ',' + ty
    if (tileLabelMap.has(key)) return

    const b36 = seedToBase36(seed)
    const short = b36.length > 14 ? b36.slice(0, 13) + '\u2026' : b36

    const label = new Text({ text: short, style: labelStyle })
    label.anchor.set(0.5, 0.5)
    // Centered in the 4-tile black gap just above the tile grid
    label.position.set(tx * 20 * 32 + 8 * 32, ty * 20 * 32 - 2 * 32)
    label.eventMode = 'static'
    label.cursor = 'pointer'
    label.on('pointerdown', () => {
        navigator.clipboard.writeText(b36).then(() => {
            createToast({ text: 'Seed copied: ' + short, type: 'success' })
        }).catch(() => {})
    })

    tileLabelMap.set(key, label)
    editor.addOverlayChild(label)
}

function removeTileLabel(tx: number, ty: number): void {
    const key = tx + ',' + ty
    const label = tileLabelMap.get(key)
    if (label) {
        label.destroy()
        tileLabelMap.delete(key)
    }
}

function exitTileMode(): void {
    tileMode = false
    if (tilePollInterval) {
        clearInterval(tilePollInterval)
        tilePollInterval = undefined
    }
    tileWorld = undefined
    for (const label of tileLabelMap.values()) {
        label.destroy()
    }
    tileLabelMap.clear()
    loadedTiles.clear()
    tileEntityMap.clear()
    customTileEntities.clear()
}

editor
    .init(CANVAS, createToast)
    .then(async () => {
        if (localStorage.getItem('quickbarItemNames')) {
            const quickbarItems = JSON.parse(localStorage.getItem('quickbarItemNames'))
            editor.quickbarItems = quickbarItems
        }

        registerActions()

        const changeBookIndex = async (index: number): Promise<void> => {
            bp = book.selectBlueprint(index)
            await editor.loadBlueprint(bp)
        }
        changeBookForIndexSelector = initSettingsPane(editor, changeBookIndex, setTileRadius).changeBook

        initFactoryOfBabel()

        // Check URL for factory seed (base36 bigint)
        let seedFromUrl: bigint | undefined
        for (const p of params) {
            if (p.includes('factory')) {
                const val = p.split('=')[1]
                if (val) {
                    try { seedFromUrl = base36ToSeed(val) } catch (_) { /* ignore */ }
                }
            }
        }

        if (bpSource) {
            exitTileMode()
            getBlueprintOrBookFromSource(bpSource)
                .catch(error => createBPImportError(error))
                .then(bpOrBook => loadBp(bpOrBook || new Blueprint()))
                .then(() => createWelcomeMessage())
                .catch(error => createBPImportError(error))
        } else {
            await enterTileMode()
            if (seedFromUrl !== undefined) {
                // Shared seed URL — navigate to that factory's deterministic tile
                await injectSeedAndNavigate(seedFromUrl)
            }
            createWelcomeMessage()
        }
    })
    .catch(error => {
        createErrorMessage('Something went wrong.', error, Infinity)
        loadingScreen.el.classList.add('error')
        throw new Error('UNRECOVERABLE_ERROR')
    })

window.addEventListener('visibilitychange', () => {
    localStorage.setItem('quickbarItemNames', JSON.stringify(editor.quickbarItems))
})

async function loadBp(bpOrBook: Blueprint | Book): Promise<void> {
    if (bpOrBook instanceof Book) {
        book = bpOrBook
        bp = book.selectBlueprint(bpIndex ? bpIndex : undefined)
    } else {
        book = undefined
        bp = bpOrBook
    }

    await editor.loadBlueprint(bp)
    changeBookForIndexSelector(bpOrBook)

    loadingScreen.hide()

    const bpIsEmpty = bpOrBook instanceof Blueprint && bpOrBook.isEmpty()
    if (!bpIsEmpty) {
        createToast({ text: 'Blueprint string loaded successfully', type: 'success' })
        syncSeedFromCurrentBp()
    }
}

document.addEventListener('copy', (e: ClipboardEvent) => {
    if (document.activeElement !== CANVAS) return
    e.preventDefault()

    if (bp.isEmpty()) return

    // In tile mode, label with tile coords + seed so pasting round-trips
    const prevName = bp.name
    if (tileMode && tileWorld) {
        const active = tileWorld.getActiveTile(centerTX, centerTY)
        const b36 = seedToBase36(active.seed)
        bp.name = 'FoB (' + centerTX + ',' + centerTY + ') ' + b36
    }

    encode(book || bp)
        .then(s => navigator.clipboard.writeText(s))
        .then(() => {
            bp.name = prevName
            createToast({ text: 'Blueprint string copied to clipboard', type: 'success' })
        })
        .catch((error: Error) => {
            bp.name = prevName
            createErrorMessage('Blueprint string could not be generated.', error)
        })
})

document.addEventListener('paste', (e: ClipboardEvent) => {
    if (document.activeElement !== CANVAS) return
    e.preventDefault()

    loadingScreen.show()

    navigator.clipboard
        .readText()
        .then(getBlueprintOrBookFromSource)
        .then(async (bpOrBook) => {
            const pastedBp = bpOrBook instanceof Book
                ? bpOrBook.selectBlueprint(bpIndex || 0)
                : bpOrBook

            const tile = extractFobTile(pastedBp)
            if (tile && tile.tx !== undefined) {
                // Copy/paste round-trip: has tile coords — navigate there
                await navigateToTile(tile.tx, tile.ty)
            } else if (tile && tile.seed !== undefined) {
                // Has a factory seed but no tile coords:
                // decode, inject at a deterministic tile, and stay in tile world
                await injectSeedAndNavigate(tile.seed)
            } else {
                // Non-FoB blueprint: load in single-factory mode
                exitTileMode()
                await loadBp(pastedBp)
            }
        })
        .catch(error => {
            loadingScreen.hide()
            createBPImportError(error)
        })
})

function registerActions(): void {
    EDITOR.registerAction('clear', {
        trigger: { code: 'KeyN' },
        modifiers: { shift: true },
        callbacks: {
            onPress: () => {
                loadBp(new Blueprint())
                return true
            },
        },
    })

    EDITOR.registerAction('appendBlueprint', {
        trigger: { code: 'KeyV' },
        modifiers: { shift: true, control: true },
        callbacks: {
            onPress: () => {
                navigator.clipboard
                    .readText()
                    .then(getBlueprintOrBookFromSource)
                    .then(bp =>
                        editor.appendBlueprint(bp instanceof Book ? bp.selectBlueprint(0) : bp)
                    )
                    .catch(error => {
                        createBPImportError(error)
                    })
                return true
            },
        },
    })

    EDITOR.registerAction('generateOilOutpost', {
        trigger: { code: 'KeyG' },
        callbacks: {
            onPress: () => {
                const errorMessage = bp.generatePipes()
                if (errorMessage) {
                    createToast({ text: errorMessage, type: 'warning' })
                }
                return true
            },
        },
    })

    EDITOR.registerAction('takePicture', {
        trigger: { code: 'KeyS' },
        modifiers: { control: true },
        callbacks: {
            onPress: () => {
                if (bp.isEmpty()) return

                editor.getPicture().then(blob => {
                    FileSaver.saveAs(blob, `${bp.name}.png`)
                    createToast({ text: 'Blueprint image successfully generated', type: 'success' })
                })
                return true
            },
        },
    })

    window.addEventListener('keydown', e => {
        if (e.target instanceof HTMLInputElement) return
        if (e.target instanceof HTMLTextAreaElement) return
        const infoPanel = document.getElementById('info-panel')
        if (e.key === 'i') {
            if (infoPanel.classList.contains('active')) {
                infoPanel.classList.remove('active')
            } else {
                infoPanel.classList.add('active')
            }
        } else if (e.key === 'Escape') {
            infoPanel.classList.remove('active')
        }
    })

    EDITOR.importKeybinds(JSON.parse(localStorage.getItem('keybinds2')))

    window.addEventListener('visibilitychange', () => {
        const keybinds = EDITOR.exportKeybinds()
        if (Object.keys(keybinds).length) {
            localStorage.setItem('keybinds2', JSON.stringify(keybinds))
        } else {
            localStorage.removeItem('keybinds2')
        }
    })
}

// ---- Factory of Babel ----

/** Called from initFactoryOfBabel to expose seed display updates */
let setFobSeed: (seed: bigint, info?: string) => void = () => {}

/** Store original (unshifted) entities for FoB-generated blueprints so we can recover the seed */
const bpEntityCache = new WeakMap<object, IEntity[]>()

/** Recover a seed from the current blueprint.
 *  1. Check WeakMap for FoB-generated factories (exact round-trip)
 *  2. Check label for "FoB <base36>" prefix
 *  3. Fallback: remap shifted positions back to 0-15 grid and encode */
function syncSeedFromCurrentBp(): void {
    if (!bp || bp.isEmpty()) return

    const serialized = bp.serialize()
    const entities = serialized.entities
    if (!entities || entities.length === 0) return

    // 1. Exact recovery from WeakMap (FoB-generated factories)
    const cached = bpEntityCache.get(bp)
    if (cached) {
        const seed = encodeFactory(cached)
        setFobSeed(seed, `${cached.length} entities`)
        return
    }

    // 2. Parse from label
    const match = bp.name.match(/^FoB ([0-9a-zA-Z]+)/)
    if (match) {
        try {
            const seed = base36ToSeed(match[1])
            setFobSeed(seed, `${entities.length} entities`)
            return
        } catch (_) { /* fall through */ }
    }

    // 3. Fallback: snap shifted positions back to integer grid by subtracting min
    let minX = Infinity, minY = Infinity
    for (const e of entities) {
        minX = Math.min(minX, e.position.x)
        minY = Math.min(minY, e.position.y)
    }

    const gridEntities: IEntity[] = entities.map((e, i) => ({
        entity_number: i + 1,
        name: e.name,
        position: {
            x: Math.round(e.position.x - minX),
            y: Math.round(e.position.y - minY),
        },
        direction: e.direction,
        recipe: e.recipe,
    }))

    const seed = encodeFactory(gridEntities)
    setFobSeed(seed, `${entities.length} entities`)
}

function initFactoryOfBabel(): void {
    const panel = document.createElement('div')
    panel.id = 'fob-panel'
    panel.innerHTML = `
        <div class="fob-header">
            <span class="fob-title">🧬 Factory of Babel</span>
            <button id="fob-close" title="Close">&times;</button>
        </div>
        <div class="fob-body">
            <div class="fob-info" id="fob-info"></div>
            <div class="fob-seed-row">
                <label for="fob-seed-input">Seed</label>
                <input type="text" id="fob-seed-input" placeholder="e.g. 42" />
                <button id="fob-random-btn" title="Random seed">🎲</button>
            </div>
            <div class="fob-nav">
                <button id="fob-prev-btn" title="Previous factory">◀ Prev</button>
                <button id="fob-regen-btn">🔄 Generate</button>
                <button id="fob-next-btn" title="Next factory">Next ▶</button>
            </div>
            <div class="fob-actions">
                <button id="fob-copy-btn" class="fob-action-btn">📋 Copy BP String</button>
            </div>
        </div>
    `
    document.body.appendChild(panel)

    const seedInput = panel.querySelector('#fob-seed-input') as HTMLInputElement
    const infoEl = panel.querySelector('#fob-info') as HTMLElement

    const updateSeedDisplay = (seed: bigint, info?: string): void => {
        const b36 = seedToBase36(seed)
        seedInput.value = b36
        currentFactorySeed = seed
        infoEl.textContent = info || `Seed: ${b36.slice(0, 20)}…`
        const url = new URL(window.location.href)
        url.searchParams.set('factory', b36)
        window.history.replaceState({}, '', url.toString())
    }

    setFobSeed = updateSeedDisplay

    const loadSeed = async (seed: bigint): Promise<void> => {
        updateSeedDisplay(seed)
        try {
            await injectSeedAndNavigate(seed)
        } catch (e) {
            createErrorMessage('Failed to load seed.', e)
        }
    }

    // Close button
    panel.querySelector('#fob-close')!.addEventListener('click', () => {
        panel.classList.toggle('fob-collapsed')
    })

    // Seed input
    seedInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            try {
                const seed = base36ToSeed(seedInput.value)
                if (seed !== undefined) void loadSeed(seed)
            } catch (_) { /* invalid */ }
        }
    })

    // Random tile
    panel.querySelector('#fob-random-btn')!.addEventListener('click', async () => {
        if (tileMode && tileWorld) {
            const newTX = Math.floor(Math.random() * 2001) - 1000
            const newTY = Math.floor(Math.random() * 2001) - 1000
            await syncTiles(newTX, newTY)
        } else {
            const result = generateFactory()
            updateSeedDisplay(result.seed, 'Random (' + result.entities.length + ' entities)')
            await loadFactoryFromSeed(result.seed)
        }
    })

    // Generate button — if the seed field was edited, honor it, otherwise
    // re-sync the window (tile mode) or generate a random factory (single mode)
    panel.querySelector('#fob-regen-btn')!.addEventListener('click', async () => {
        try {
            const seed = base36ToSeed(seedInput.value)
            if (seed !== undefined && seed !== currentFactorySeed) {
                await loadSeed(seed)
                return
            }
        } catch (_) { /* invalid seed, fall through */ }

        if (tileMode && tileWorld) {
            await syncTiles(centerTX, centerTY)
        } else {
            const result = generateFactory()
            updateSeedDisplay(result.seed, 'Random (' + result.entities.length + ' entities)')
            await loadFactoryFromSeed(result.seed)
        }
    })

    // Prev/Next — navigate tile neighbors (or similar seeds in single mode)
    panel.querySelector('#fob-prev-btn')!.addEventListener('click', () => {
        if (tileMode && tileWorld) {
            void syncTiles(centerTX - 1, centerTY)
        } else if (currentFactorySeed !== undefined) {
            loadSeed(neighborSeed(currentFactorySeed, 'prev'))
        }
    })
    panel.querySelector('#fob-next-btn')!.addEventListener('click', () => {
        if (tileMode && tileWorld) {
            void syncTiles(centerTX + 1, centerTY)
        } else if (currentFactorySeed !== undefined) {
            loadSeed(neighborSeed(currentFactorySeed, 'next'))
        }
    })

    // Copy blueprint string
    panel.querySelector('#fob-copy-btn')!.addEventListener('click', async () => {
        if (!bp || bp.isEmpty()) {
            createToast({ text: 'No factory generated yet!', type: 'warning' })
            return
        }
        try {
            const bpString = await encode(bp)
            await navigator.clipboard.writeText(bpString)
            createToast({ text: 'Blueprint string copied to clipboard!', type: 'success' })
        } catch (e) {
            createErrorMessage('Could not generate blueprint string.', e)
        }
    })

    // Keyboard shortcut: Ctrl+G to toggle panel, or G to generate if panel is open
    window.addEventListener('keydown', (e) => {
        if (e.target instanceof HTMLInputElement) return
        if (e.target instanceof HTMLTextAreaElement) return
        if (e.ctrlKey && e.key === 'g') {
            e.preventDefault()
            panel.classList.toggle('fob-collapsed')
            if (!panel.classList.contains('fob-collapsed')) {
                seedInput.focus()
            }
        }
    })
}

async function loadFactoryFromSeed(seed: bigint): Promise<void> {
    loadingScreen.show()
    try {
        exitTileMode()

        const result = loadFactory(seed)
        const b36 = seedToBase36(seed)

        const factoryBp = new Blueprint({
            label: 'FoB ' + b36,
            entities: result.entities,
            version: getFactorioVersion(),
            icons: [{ index: 1, signal: { type: 'item', name: 'assembling-machine-1' } }],
        })

        book = undefined
        bp = factoryBp
        bpEntityCache.set(factoryBp, result.entities)

        await editor.loadBlueprint(factoryBp)
        changeBookForIndexSelector(factoryBp)

        // loadBlueprint restores the old viewport, so recenter on the factory
        // to actually bring it into view
        let cx = 256
        let cy = 256
        if (result.entities.length > 0) {
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
            for (const e of result.entities) {
                minX = Math.min(minX, e.position.x)
                minY = Math.min(minY, e.position.y)
                maxX = Math.max(maxX, e.position.x)
                maxY = Math.max(maxY, e.position.y)
            }
            cx = ((minX + maxX) / 2 + 0.5) * 32
            cy = ((minY + maxY) / 2 + 0.5) * 32
        }
        editor.centerViewportOn(cx, cy, Math.min(editor.getViewportScale(), 1.5))

        loadingScreen.hide()
        setFobSeed(seed, result.entities.length + ' entities')
        createToast({
            text: 'Factory loaded! (' + result.entities.length + ' entities)',
            type: 'success',
        })
    } catch (e) {
        loadingScreen.hide()
        createErrorMessage('Failed to generate factory.', e)
    }
}

function createWelcomeMessage(): void {
    const notFirstRun = localStorage.getItem('firstRun') === 'false'
    if (notFirstRun) return
    localStorage.setItem('firstRun', 'false')

    // Wait a bit just to capture the users attention
    // This way they will see the toast animation
    setTimeout(() => {
        createToast({
            text:
                '> To access the inventory and start building press E<br>' +
                '> To import/export a blueprint string use ctrl/cmd + C/V<br>' +
                '> For more info press I<br>' +
                '> Also check out the settings area',
            timeout: 30000,
        })
    }, 1000)
}
function createErrorMessage(text: string, error: unknown, timeout = 10000): void {
    console.error(error)
    createToast({
        text:
            `${text}<br>` +
            'Please check out the console (F12) for an error message and ' +
            'report this bug on github or using the feedback button.',
        type: 'error',
        timeout,
    })
}
function createBPImportError(
    error:
        | Error
        | TrainBlueprintError
        | ModdedBlueprintError
        | CorruptedBlueprintStringError
        | BookWithNoBlueprintsError
): void {
    if (error instanceof TrainBlueprintError) {
        createErrorMessage(
            'Blueprint with train entities not supported yet. If you think this is a mistake:',
            error.errors
        )
        return
    }

    if (error instanceof ModdedBlueprintError) {
        createErrorMessage(
            'Blueprint with modded items not supported yet. If you think this is a mistake:',
            error.errors
        )
        return
    }

    if (error instanceof CorruptedBlueprintStringError) {
        createErrorMessage(
            'Blueprint string might be corrupted. If you think this is a mistake:',
            error.error
        )
        return
    }

    if (error instanceof BookWithNoBlueprintsError) {
        createErrorMessage(`${error.error} If you think this is a mistake:`, error.error)
        return
    }

    createErrorMessage('Blueprint string could not be loaded.', error)
}
